import { Component, Input, Output, EventEmitter, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoStepCache } from '../fiko-performance/FikoStepCache';

/**
 * OpenCascade Interface_Static okuma parametreleri
 */
export interface OpenCascadeReadParams {
    /** Surface curve modu (0: default, 3: 3D preferred) */
    surfaceCurveMode: number;
    /** Precision modu (0: file, 1: user) */
    precisionMode: number;
    /** Precision değeri (mm) */
    precisionVal: number;
    /** Maksimum precision değeri (mm) */
    maxPrecisionVal: number;
    /** Product modu ("0" veya "1") */
    productMode: string;
    /** Assembly level (0, 1, 2) */
    assemblyLevel: number;

    // Tessellation parametreleri
    /** Multi-threaded tessellation (true/false) */
    inParallel?: boolean;
    /** Minimum triangle boyutu (mm) */
    minSize?: number;
    /** İç vertex'leri kullan */
    internalVerticesMode?: boolean;
    /** Yüzey sapma kontrolü */
    controlSurfaceDeflection?: boolean;

    // Encoding & Regularity
    /** Açı birimi kontrolü (varsayılan 0.01) */
    encodeRegularityAngle?: number;

    // Step Schema & Version
    /** STEP şeması (AP203, AP214, AP242) */
    stepSchema?: string;
    /** Shape processing sıralaması */
    stepSequence?: string;
    /** Resource dosyası */
    resourceName?: string;

    // Advanced Reading
    /** Non-manifold okuma (0/1) */
    nonManifold?: number;
    /** IDEAS format uyumluluğu (0/1) */
    ideasMode?: number;
    /** Tüm shape'leri oku (true/false) */
    allShapes?: boolean;

    // Unit
    /** Birim dönüşümü (MM/CM/M/INCH/FT) */
    unit?: string;

    /** Shape healing — ShapeFix_Shape */
    shapeHealing?: boolean;
    /** Bodyleri birleştir (solidleri tek mesh) */
    mergeBodies?: boolean;
    /** Kısa edge toleransı (mm) */
    shortEdgeTolerance?: number;
}

/**
 * STP Import ayarları
 *
 * STEP dosyası import parametreleri.
 * OpenCascade BRepMesh_IncrementalMesh ve IMeshTools_Parameters ile uyumlu.
 *
 * Import Yöntemleri:
 * - occt-import-js: Hafif WASM modülü (~2MB), hızlı, renk desteği sınırlı
 * - opencascade-js: Tam OpenCascade.js (~65MB), CAF/XDE renk desteği, daha yavaş
 *
 * @see https://github.com/kovacsv/occt-import-js
 * @see https://github.com/nicholasspencer/opencascade.js
 */
export interface STPImportOptions {
    /**
     * Import yöntemi
     * - 'occt-import-js': Hızlı, hafif (~2MB WASM)
     * - 'opencascade-js': Tam özellikli (~65MB WASM), renk desteği
     */
    importMethod: 'occt-import-js' | 'opencascade-js';

    /**
     * Lineer sapma (mm cinsinden)
     * Mesh noktaları ile gerçek yüzey arasındaki max mesafe.
     * Düşük = Kaliteli, Yüksek = Hızlı
     * @default 0.1
     */
    linearDeflection: number;

    /**
     * Açısal sapma (derece cinsinden - radyana çevrilir)
     * Not: Dialog'da derece olarak girilir, emit sırasında radyana çevrilir
     * @default 30
     */
    angularDeflection: number;

    /**
     * Göreceli mod
     * true: Bounding box'a göreceli
     * false: Mutlak mm değerleri
     * @default false
     */
    relativeMode: boolean;

    /** Gruplama tipi (montaj yapısı) */
    gruplama: 'yok' | 'hiyerarşik';

    /** XCAF renk bilgilerini kullan */
    useColors: boolean;

    /** OpenCascade Interface_Static okuma parametreleri (sadece opencascade-js için) */
    readParams?: OpenCascadeReadParams;

    /** Sharp edge tespit eşiği (derece) — varsayılan 30 */
    edgeThresholdAngle?: number;
    /** Import sonrası modeli dünya merkezine al */
    autoCenter?: boolean;
    /** Import sonrası kameraya sığdır (fit-to-view) */
    autoFit?: boolean;
    /** Ölçek çarpanı (1 = değişim yok) */
    scaleFactor?: number;
    /** Çift yüzeyli materyal (arka yüzler görünsün) */
    doubleSided?: boolean;
    /** Normal yumuşatma (computeVertexNormals) */
    smoothNormals?: boolean;
    /** XCAF rengi yoksa kullanılacak varsayılan renk (hex, örn #cccccc) */
    defaultColor?: string;
    /** Varsayılan opaklık 0-1 */
    defaultOpacity?: number;
}

/**
 * STP Import Seçenekleri Dialog Component
 *
 * STP dosyası seçildiğinde açılan dialog.
 * Kullanıcıya import yöntemi ve tessellation parametrelerini sunar.
 */
@Component({
    selector: 'fikoyt-stp-import-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './stp-import-dialog.component.html',
    styleUrls: ['./stp-import-dialog.component.scss']
})
export class StpImportDialogComponent implements OnInit {
    id_dialog = "dialogStpImport" + this.FikoYT.fikoIdUret();

    /** Dialog Core'dan gelen pencere referansı */
    @Input() pencere: any;

    /** Yüklenecek dosya */
    @Input() dosya: File | null = null;

    /** Çoklu import için dosya sayısı */
    @Input() dosyaSayisi: number = 1;

    /** Çoklu import için dosya isimleri listesi */
    @Input() dosyaListesi: string[] = [];

    /** CAF/XDE renk desteği var mı? */
    @Input() hasColorSupport: boolean = false;

    /** Import callback - parametreler üzerinden geçirilir (ngComponentOutlet output desteklemediği için) */
    @Input() onImport?: (options: STPImportOptions) => void;

    /** Cancel callback - parametreler üzerinden geçirilir */
    @Input() onCancel?: () => void;

    /** Import başladığında (legacy - ngComponentOutlet ile çalışmaz) */
    @Output() importBasladi = new EventEmitter<STPImportOptions>();

    /** Dialog kapandığında (legacy - ngComponentOutlet ile çalışmaz) */
    @Output() kapandi = new EventEmitter<void>();

    // Import ayarları - varsayılanlar
    options: STPImportOptions = {
        importMethod: 'occt-import-js',
        linearDeflection: 0.1,    // mm
        angularDeflection: 30,    // derece
        relativeMode: false,
        gruplama: 'yok',
        useColors: true,
        edgeThresholdAngle: 30,
        autoCenter: false,
        autoFit: true,
        scaleFactor: 1,
        doubleSided: false,
        smoothNormals: true,
        defaultColor: '#cccccc',
        defaultOpacity: 1,
        readParams: {
            surfaceCurveMode: 3,              // 3D eğrileri tercih et
            precisionMode: 1,                 // User mode
            precisionVal: 0.01,               // 0.01mm tolerans
            maxPrecisionVal: 1.0,             // Max 1.0mm
            productMode: '1',                 // Product mode ON
            assemblyLevel: 1,                 // Assembly level 1

            // Tessellation
            inParallel: true,                 // Multi-threaded tessellation
            minSize: 0.01,                    // Min triangle size 0.01mm
            internalVerticesMode: true,       // İç vertex'leri kullan
            controlSurfaceDeflection: true,   // Yüzey sapma kontrolü

            // Encoding & Regularity
            encodeRegularityAngle: 0.01,      // Açı birimi kontrolü

            // Step Schema
            stepSchema: 'AP214',              // STEP şeması (AP214 en yaygın)
            stepSequence: '',                 // Varsayılan
            resourceName: '',                 // Varsayılan

            // Advanced Reading
            nonManifold: 0,                   // Non-manifold okuma kapalı
            ideasMode: 0,                     // IDEAS uyumluluk kapalı
            allShapes: true,                  // Tüm shape'leri oku

            // Unit
            unit: 'MM',                       // Birim: MM

            shapeHealing: false,
            mergeBodies: false,
            shortEdgeTolerance: 0.01
        }
    };

    private readonly AYAR_KEY = 'fikoyt.stp-import.options';

    /**
     * Tessellation kalite preset'leri
     * OpenCascade BRepMesh_IncrementalMesh parametreleri ile uyumlu
     *
     * linear: linearDeflection (mm) - mesh ile yüzey arası max mesafe
     * angular: angularDeflection (derece) - segment'ler arası max açı
     *
     * Düşük değerler = Daha kaliteli mesh, daha fazla triangle, daha yavaş
     * Yüksek değerler = Daha az triangle, daha hızlı, daha kaba görünüm
     */
    kalitePresetleri = [
        { isim: 'Ultra Hızlı', linear: 2.0, angular: 60, aciklama: 'Ön izleme, çok büyük dosyalar (>100MB)' },
        { isim: 'Hızlı', linear: 0.5, angular: 45, aciklama: 'Büyük dosyalar (10-100MB)' },
        { isim: 'Normal', linear: 0.1, angular: 30, aciklama: 'Standart kalite (önerilen)' },
        { isim: 'Yüksek', linear: 0.05, angular: 15, aciklama: 'Detaylı görünüm' },
        { isim: 'Çok Yüksek', linear: 0.01, angular: 5, aciklama: 'Maksimum detay, yavaş' },
        { isim: 'Adaptif', linear: -1, angular: -1, aciklama: 'Dosya boyutuna göre otomatik seçim' },
        { isim: 'Manuel', linear: -2, angular: -2, aciklama: 'Kullanıcı tanımlı değerler' }
    ];
    seciliPreset = 2; // Normal (varsayılan)

    /** Manuel preset indeksi (sentinel -2) */
    get manuelPresetIndex(): number {
        return this.kalitePresetleri.findIndex(p => p.linear === -2);
    }

    // Android-style settings panel
    ayarlarPanelAcik = false;
    aktifAyarPaneli: 'tessellation' | 'gelismis' | null = null;

    // Dosya bilgisi
    dosyaBoyutu = '';

    constructor(
        public readonly FikoYT: FikoYTService,
        public readonly CD: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // Dosya boyutunu hesapla
        if (this.dosya) {
            const bytes = this.dosya.size;
            if (bytes < 1024) {
                this.dosyaBoyutu = bytes + ' B';
            } else if (bytes < 1024 * 1024) {
                this.dosyaBoyutu = (bytes / 1024).toFixed(1) + ' KB';
            } else {
                this.dosyaBoyutu = (bytes / 1024 / 1024).toFixed(2) + ' MB';
            }
        }

        // Çoklu dosya için dosya sayısını göster
        if (this.dosyaSayisi > 1 && !this.dosyaListesi.length) {
            this.dosyaListesi = [this.dosya?.name || 'Dosya'];
        }

        // Varsayılan olarak occt-import-js seçili (hızlı ve hafif)
        // OpenCascade.js artık dialog açılırken yüklenmiyor, kullanıcı seçerse yüklenecek
        this.options.importMethod = 'occt-import-js';

        try {
            const kayitli = localStorage.getItem(this.AYAR_KEY);
            if (kayitli) {
                const parsed = JSON.parse(kayitli);
                if (parsed && typeof parsed === 'object') {
                    this.options = {
                        ...this.options,
                        ...parsed,
                        readParams: {
                            ...this.options.readParams!,
                            ...(parsed.readParams || {})
                        }
                    };
                }
            }
        } catch {
            // Şema mismatch durumunda varsayılanlarla devam et
        }
    }

    /** Çoklu dosya modu mu? */
    get cokluDosyaModu(): boolean {
        return this.dosyaSayisi > 1;
    }

    /**
     * Kalite preset'i degistiginde
     */
    presetDegisti(): void {
        const preset = this.kalitePresetleri[this.seciliPreset];
        if (preset) {
            if (preset.linear === -1) {
                // Adaptif: dosya boyutuna gore otomatik kalite sec
                this.adaptifKaliteHesapla();
            } else if (preset.linear === -2) {
                // Manuel: mevcut değerlere dokunma
                return;
            } else {
                this.options.linearDeflection = preset.linear;
                this.options.angularDeflection = preset.angular;
            }
        }
    }

    /**
     * Kullanıcı linear/angular değerini elle değiştirdiğinde Manuel preset'e geç
     */
    manuelDegerDegisti(): void {
        this.seciliPreset = this.manuelPresetIndex;
    }

    /**
     * Dosya boyutuna gore adaptif tessellation kalitesi hesapla.
     * Buyuk dosyalar icin dusuk kalite, kucuk dosyalar icin yuksek kalite.
     */
    private adaptifKaliteHesapla(): void {
        const bytes = this.dosya?.size || 0;
        const mb = bytes / (1024 * 1024);

        if (mb > 100) {
            this.options.linearDeflection = 2.0;
            this.options.angularDeflection = 60;
        } else if (mb > 30) {
            this.options.linearDeflection = 0.5;
            this.options.angularDeflection = 45;
        } else if (mb > 5) {
            this.options.linearDeflection = 0.1;
            this.options.angularDeflection = 30;
        } else if (mb > 0.5) {
            this.options.linearDeflection = 0.05;
            this.options.angularDeflection = 15;
        } else {
            this.options.linearDeflection = 0.01;
            this.options.angularDeflection = 5;
        }
    }

    /**
     * Import yöntemi değiştiğinde
     */
    importMethodDegisti(): void {
        // OpenCascade.js seçildiğinde renk desteğini aktif et
        if (this.options.importMethod === 'opencascade-js') {
            this.options.useColors = true;
        } else {
            // occt-import-js seçilirse ayarlar panelini kapat
            this.ayarlarPanelAcik = false;
            this.aktifAyarPaneli = null;
        }
    }

    /**
     * Ayarlar panelini aç
     */
    ayarlarPanelAc(panel: 'tessellation' | 'gelismis'): void {
        if (this.options.importMethod !== 'opencascade-js') return;
        this.aktifAyarPaneli = panel;
        this.ayarlarPanelAcik = true;
    }

    /**
     * Ayarlar panelini kapat
     */
    ayarlarPanelKapat(): void {
        this.ayarlarPanelAcik = false;
        setTimeout(() => {
            this.aktifAyarPaneli = null;
        }, 300); // Animation süresi
    }

    /**
     * Import başlat
     */
    importBaslat(): void {
        try {
            localStorage.setItem(this.AYAR_KEY, JSON.stringify(this.options));
        } catch {
            // Kota/disable durumunda sessizce geç
        }

        // Açısal sapma'yı derece'den radyan'a çevir
        const optionsToEmit: STPImportOptions = {
            ...this.options,
            angularDeflection: this.options.angularDeflection * (Math.PI / 180)
        };

        // Callback varsa çağır (ngComponentOutlet için)
        if (this.onImport) {
            this.onImport(optionsToEmit);
        }

        // Legacy emit (doğrudan component kullanımı için)
        this.importBasladi.emit(optionsToEmit);
        this.kapa();
    }

    /**
     * Dialog kapat (iptal)
     */
    public kapa(): void {
        // Cancel callback varsa çağır
        if (this.onCancel) {
            this.onCancel();
        }

        // Legacy emit
        this.kapandi.emit();

        // Dialog pencereyi kapat
        if (this.pencere?.instance?.kapa) {
            this.pencere.instance.kapa();
        } else if (this.pencere?.kapa) {
            this.pencere.kapa();
        }
    }

    /** Önbellek temizlendi göstergesi (UI feedback) */
    public onbellekTemizlendi = false;

    /** Kalıcı geometri (tesselation) önbelleğini temizler */
    public async onbellekTemizle(): Promise<void> {
        await FikoStepCache.temizle();
        this.onbellekTemizlendi = true;
        this.CD.detectChanges();
        setTimeout(() => { this.onbellekTemizlendi = false; this.CD.detectChanges(); }, 2500);
    }

    trackByPresetIsim(index: number, preset: any): string {
        return preset.isim;
    }

    trackByDosyaIsim(index: number, isim: string): string {
        return isim;
    }
}
